/**
 * User-friendly error messages for common errors
 */

export const getErrorMessage = (error: any, defaultMessage: string = 'An error occurred'): string => {
  if (!error) return defaultMessage;

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Handle Error objects
  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    // Network errors
    if (message.includes('network') || message.includes('fetch')) {
      return 'Network error. Please check your internet connection and try again.';
    }

    if (message.includes('timeout')) {
      return 'Request timed out. Please try again.';
    }

    // Authentication errors
    if (message.includes('unauthorized') || message.includes('authentication')) {
      return 'Your session has expired. Please log in again.';
    }

    if (message.includes('forbidden') || message.includes('permission')) {
      return 'You do not have permission to perform this action.';
    }

    // Not found errors
    if (message.includes('not found') || message.includes('404')) {
      return 'The requested resource was not found.';
    }

    // Validation errors
    if (message.includes('validation') || message.includes('invalid')) {
      return 'Please check your input and try again.';
    }

    // Database errors
    if (message.includes('database') || message.includes('sql')) {
      return 'A database error occurred. Please try again later.';
    }

    // Return the error message if it's user-friendly
    if (error.message && error.message.length < 100) {
      return error.message;
    }
  }

  // Handle API error responses
  if (error.response) {
    const status = error.response.status;
    const data = error.response.data;

    if (data?.message) {
      return data.message;
    }

    switch (status) {
      case 400:
        return 'Invalid request. Please check your input.';
      case 401:
        return 'Your session has expired. Please log in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'This action conflicts with existing data.';
      case 422:
        return 'Validation error. Please check your input.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Please try again later.';
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return `Error ${status}. Please try again.`;
    }
  }

  // Handle Supabase errors
  if (error.code) {
    switch (error.code) {
      case 'PGRST116':
        return 'The requested resource was not found.';
      case '23505':
        return 'This record already exists.';
      case '23503':
        return 'Invalid reference. Please check your input.';
      default:
        if (error.message) {
          return error.message;
        }
    }
  }

  return defaultMessage;
};

export const getRetryMessage = (attempts: number, maxAttempts: number = 3): string => {
  if (attempts >= maxAttempts) {
    return 'Maximum retry attempts reached. Please refresh the page.';
  }
  return `Retrying... (${attempts}/${maxAttempts})`;
};



